import { IDropdownOption } from '@fluentui/react';
import { Formik, FormikProps, useFormikContext } from 'formik';
import * as React from 'react';
import { useEffect, useState } from 'react';
import * as yup from 'yup';
import { Schema } from 'yup';

import {
  CreateFrequencySpectrumDto,
  LocationConfigDto,
  LocationSearchResult,
  MaintainFrequencySpectrumDto,
  RadioLicenceDto,
  ReceiveLocationDto,
  SpectrumDto,
  SpectrumDtoSpectrumTypeEnum,
  TransmitLocationDto,
} from 'api-client';

import Card from 'common/controls/surfaces/Card';
import Accordion from 'common/layout/Accordion';
import { useLicenceCraftingApi } from 'licence_management/craft/LicenceCraftingApiHook';

import { TextField } from '../../../../common/controls/inputs/TextField';
import Error from '../../../../common/controls/items/Error';
import Form, { FormContext } from '../../../../common/form/Form';
import { SubSectionHeading } from '../../../../common/layout/SectionHeadings';
import { isEmpty } from '../../../../common/utils/objectUtils';
import { validateWithFormValues } from '../../../../common/validation/validationWithFormValues';
import EmbeddedSearchLocation from '../../../../search/location/EmbeddedSearchLocation';
import { LocationTypeOptionsEnum } from '../../../../search/location/SearchLocationForm';
import { Grid } from '../../../../ui-library';
import { useLicenceApi } from '../../../LicenceApiHooks';
import { useDoneValidation } from '../../../craft/DoneValidationHooks';
import { spectrumsAsFormValues } from '../../../craft/components/SpectrumDetails/LicenceSpectrumDetails';
import LicenceLocationList from '../../../craft/components/licence_locations/common/LocationsList';
import SpectrumDetailsForm from './SpectrumDetailsForm';
import {
  receiveLocationDoneSchema,
  getSatelliteReceiveLocationsSchema,
  satelliteReceiveLocationAllDoneSchema,
  satelliteSpectrumDetailsSchema,
  satelliteTransmitLocationAllDoneSchema,
  satelliteTransmitLocationsSchema,
  transmitLocationDoneSchema,
} from './validationSchema';

interface Props {
  locationType: 'TRANSMIT' | 'RECEIVE';
  licence: RadioLicenceDto;
  onDone: (isDone: boolean) => Promise<void>;
  isPreviousSectionsDone: boolean;
  locationConfiguration: LocationConfigDto;
  onSelectDeselectTransmitLocation?: (isMandatory: boolean) => void;
  autoPopulateLocationId?: number;
}

type SubmitState = {
  onSubmit: (values: any) => Promise<any>;
  schema: Schema<any> | ((values: any) => Schema<any>);
  isDone?: boolean;
};

const LocationSection: React.FC<Props> = (props) => {
  const { createFrequencySpectrum, getSpectrums, maintainFrequencySpectrum } = useLicenceApi();

  const {
    maintainReceiveLocation,
    maintainTransmitLocation,
    removeLicenceConfiguration,
    createReceiveLocation,
    createTransmitLocation,
    getReceiveLocations,
    getTransmitLocations,
    removeSpectrum,
  } = useLicenceCraftingApi();

  const [locations, setLocations] = React.useState<TransmitLocationDto[] | ReceiveLocationDto[]>([]);
  const location = locations?.length ? locations[0] : undefined;

  const [spectrums, setSpectrumsState] = useState<SpectrumDto[]>([]);
  const setSpectrums = (spectrums: SpectrumDto[]) => setSpectrumsState(spectrumsAsFormValues(spectrums));
  const spectrum = spectrums?.length ? spectrums[0] : undefined;

  const [expanded, setExpanded] = useState<boolean | undefined>();

  const isDone =
    props.locationType === 'TRANSMIT'
      ? !!props.licence?.craftingProgress?.isTransmitLocationDetailsDone
      : !!props.licence?.craftingProgress?.isReceiveLocationDetailsDone;

  const allDoneSchema = props.locationType === 'TRANSMIT' ? satelliteTransmitLocationAllDoneSchema : satelliteReceiveLocationAllDoneSchema;

  const spectrumSectionTitle = props.locationType === 'TRANSMIT' ? 'Create a spectrum record (Transmit)' : 'Create a spectrum record (Receive)';

  const prevAutoPopulateLocationId = React.useRef<number | undefined>(undefined);
  const [viewLocationId, setViewLocationId] = useState<number | undefined>();
  const [lastSaved, setLastSaved] = useState<Date | undefined>();

  const submitRef = React.useRef<SubmitState>({
    onSubmit: () => Promise.resolve(),
    schema: yup.object(),
  });
  const setSubmit = (submitState: SubmitState) => {
    submitRef.current = submitState;
  };

  // Preserve any unsaved spectrum details changes following save location
  const continueEditingSpectrumDetails = ({ spectrum }: { spectrum?: SpectrumDto }) => {
    if (spectrum) {
      setSpectrums([spectrum]);
    }
  };

  const validateDoneRef = React.useRef(false);

  const submitDone = {
    isDone: true,
    schema: allDoneSchema,
    onSubmit: (values: {
      spectrum?: MaintainFrequencySpectrumDto & CreateFrequencySpectrumDto;
      locations?: TransmitLocationDto[] | ReceiveLocationDto[];
    }) => onDone(values.spectrum!, values.locations ? values.locations[0] : undefined),
  };

  const continueEditingLocationDetails = ({ locations }: { locations?: TransmitLocationDto[] | ReceiveLocationDto[] }) => {
    if (locations) {
      setLocations(locations);
    }
  };

  const submitSpectrum = {
    schema: satelliteSpectrumDetailsSchema,
    onSubmit: ({
      spectrum,
      locations,
    }: {
      spectrum: MaintainFrequencySpectrumDto & CreateFrequencySpectrumDto;
      locations?: TransmitLocationDto[] | ReceiveLocationDto[];
    }) =>
      handleSaveSpectrum(spectrum).then((saved) => {
        continueEditingLocationDetails({ locations });
        return saved;
      }),
  };

  const submitLocation = {
    schema:
      props.locationType === 'TRANSMIT' ? satelliteTransmitLocationsSchema : getSatelliteReceiveLocationsSchema(String(props.licence.classification)),
    onSubmit: ({ locations, spectrum }: { locations: TransmitLocationDto[] | ReceiveLocationDto[]; spectrum?: SpectrumDto }) =>
      handleSaveLocation(locations[0]).then((saved) => {
        continueEditingSpectrumDetails({ spectrum });
        return saved;
      }),
  };

  const onDone = async (
    updatingSpectrum: MaintainFrequencySpectrumDto & CreateFrequencySpectrumDto,
    updatingLocation?: TransmitLocationDto | ReceiveLocationDto,
  ) => {
    const canDone1 = !!(await handleSaveSpectrum(updatingSpectrum));
    const canDone2 = updatingLocation ? !!(await handleSaveLocation(updatingLocation)) : true;
    return canDone1 && canDone2 ? props.onDone(true) : undefined;
  };

  useEffect(() => {
    if (props.locationType === 'TRANSMIT') {
      getTransmitLocations(props.licence.id!, { showLoadingSpinner: true }).then(setLocations);
    } else if (!props.autoPopulateLocationId) {
      getReceiveLocations(props.licence.id!, { showLoadingSpinner: true }).then(setLocations);
    }
    getSpectrums(props.licence.id!, { showLoadingSpinner: true }).then(setSpectrums);
  }, [props.licence]);

  useEffect(() => {
    const prev = prevAutoPopulateLocationId.current;
    prevAutoPopulateLocationId.current = props.autoPopulateLocationId;

    if (!props.autoPopulateLocationId) {
      // Location was cleared — remove existing receive location if one exists
      if (prev !== undefined) {
        getReceiveLocations(props.licence.id!, { showLoadingSpinner: true }).then((locs) => {
          const existing = locs?.[0];
          if (existing) {
            removeLicenceConfiguration(props.licence.id!, existing.id!)
              .then(() => setLocations([]))
              .then(() => props.onDone(false));
          }
        });
      }
      return;
    }

    getReceiveLocations(props.licence.id!, { showLoadingSpinner: true }).then((locs) => {
      const existing = locs?.[0];
      if (!existing) {
        createReceiveLocation(props.licence.id!, props.autoPopulateLocationId!).then(() => getReceiveLocations(props.licence.id!).then(setLocations));
      } else if (existing.locationId !== props.autoPopulateLocationId) {
        removeLicenceConfiguration(props.licence.id!, existing.id!)
          .then(() => createReceiveLocation(props.licence.id!, props.autoPopulateLocationId!))
          .then(() => getReceiveLocations(props.licence.id!).then(setLocations))
          .then(() => props.onDone(false));
      } else {
        setLocations(locs);
      }
    });
  }, [props.autoPopulateLocationId, props.licence.id]);

  const onContinueCrafting = () => props.onDone(false);

  useEffect(() => {
    if (expanded === undefined) {
      setExpanded(props.isPreviousSectionsDone && !isDone);
    } else {
      setExpanded(!isDone);
    }
  }, [props.isPreviousSectionsDone, !!isDone]);

  const handleSelectLocation = async (location: LocationSearchResult) => {
    if (props.locationType === 'TRANSMIT') {
      await createTransmitLocation(props.licence.id!, location.id);
      if (props.onSelectDeselectTransmitLocation) {
        props.onSelectDeselectTransmitLocation(location.locationType === 'A');
      }
      getTransmitLocations(props.licence.id!).then(setLocations);
    } else {
      await createReceiveLocation(props.licence.id!, location.id);
      getReceiveLocations(props.licence.id!).then(setLocations);
    }
  };

  const handleRemoveLocation = (loc: TransmitLocationDto | ReceiveLocationDto) =>
    removeLicenceConfiguration(props.licence.id!, loc.id!).then(() => {
      setLocations(locations.filter((r) => r.id !== loc.id));
      if (props.locationType === 'TRANSMIT') {
        if (props.onSelectDeselectTransmitLocation) {
          props.onSelectDeselectTransmitLocation(false);
        }
      }
    });

  const handleSaveLocation = (loc: TransmitLocationDto | ReceiveLocationDto) => {
    const updateLocations = (updatedLocs: TransmitLocationDto[] | ReceiveLocationDto[]) => {
      if (updatedLocs) {
        setLocations(updatedLocs);
        setLastSaved(new Date());
      }
      return updatedLocs[0];
    };

    return props.locationType === 'TRANSMIT'
      ? maintainTransmitLocation(props.licence.id!, loc.id!, {
          ...loc,
          licenceId: props.licence.id,
          antennaId: loc.antenna?.id,
          equipmentId: loc.equipment?.id,
        } as any).then(() => {
          return getTransmitLocations(props.licence.id || 0).then(updateLocations);
        })
      : maintainReceiveLocation(props.licence.id!, loc.id!, {
          ...loc,
          licenceId: props.licence.id,
          antennaId: loc.antenna?.id,
          equipmentId: loc.equipment?.id,
        } as any).then(() => {
          return getReceiveLocations(props.licence.id || 0).then(updateLocations);
        });
  };

  const handleSaveSpectrum = (spm: MaintainFrequencySpectrumDto & CreateFrequencySpectrumDto) => {
    spm = transformSpectrumDto(spm);
    const updateSpectrum = (updatedSpectrum: SpectrumDto) => {
      if (updatedSpectrum) {
        setSpectrums([updatedSpectrum]);
        setLastSaved(new Date());
      }
      return updatedSpectrum;
    };

    return (spm.id ? maintainFrequencySpectrum(props.licence.id || 0, spm) : createFrequencySpectrum(props.licence.id!, spm)).then(updateSpectrum);
  };

  const handleRemoveSpectrum = (spectrumId: number) => {
    const removeFromSpectrums = () => {
      setSpectrums([]);
      return spectrums;
    };
    return removeSpectrum(props.licence.id || 0, spectrumId, { showLoadingSpinner: 'Removing...' }).then(removeFromSpectrums);
  };

  return (
    <Formik<{ spectrum: SpectrumDto; locations: TransmitLocationDto[] | ReceiveLocationDto[] }>
      initialValues={
        {
          spectrum: spectrum
            ? {
                ...spectrum,
                frequencies: spectrum.frequencies && spectrum.frequencies.length > 0 ? spectrum.frequencies : [emptyFrequencyObj],
              }
            : ({
                spectrumType: SpectrumDtoSpectrumTypeEnum.FRQ,
                lowerBound: undefined,
                upperBound: undefined,
                frequencies: [emptyFrequencyObj],
              } as any),
          locations: location
            ? [{ ...location }]
            : [
                {
                  id: undefined,
                  stationType: undefined,
                  antenna: undefined,
                  antennaPattern: undefined,
                  antennaPatternOther: undefined,
                },
              ],
        } as any
      }
      onSubmit={(values) => submitRef.current.onSubmit(values)}
      validate={(values) => {
        const currentSubmit = submitRef.current;
        const schema = validateDoneRef.current
          ? submitDone.schema
          : typeof currentSubmit.schema === 'function'
          ? currentSubmit.schema(values)
          : currentSubmit.schema;
        return validateWithFormValues(schema, { ...props.licence })(values);
      }}
      // enableReinitialize is intentional — any save handler that updates state must call continueEditingSpectrumDetails/continueEditingLocationDetails afterwards to preserve the other section's unsaved edits
      enableReinitialize
    >
      {(formProps: FormikProps<{ spectrum: SpectrumDto; locations: TransmitLocationDto[] | ReceiveLocationDto[] }>) => (
        <LocationSectionAccordion
          locationType={props.locationType}
          titleOverride={props.autoPopulateLocationId ? 'Satellite Earth station – Receive location' : undefined}
          onDone={
            isDone
              ? onContinueCrafting
              : () => {
                  setSubmit(submitDone);
                  formProps.submitForm();
                }
          }
          done={isDone}
          onValidateDone={() => {
            validateDoneRef.current = true;
            formProps.validateForm().then(() => (validateDoneRef.current = false));
          }}
          licence={props.licence}
          onExpandCollapsed={() => setExpanded(!expanded)}
          expanded={expanded}
        >
          {props.autoPopulateLocationId ? null : !isDone && !(locations?.length === 1) ? (
            <Grid.Row>
              <Grid.Col>
                <Card>
                  <EmbeddedSearchLocation
                    id={props.locationType + 'search-location-form'}
                    onSelect={(loc) => handleSelectLocation(loc).then(() => continueEditingSpectrumDetails(formProps.values))}
                    searchLabel="Search and Link an existing location record"
                    searchOptions={{
                      locationTypeOptions: [LocationTypeOptionsEnum.POINT, LocationTypeOptionsEnum.DEFINED_AREA],
                      hideVisibiltiy: true,
                    }}
                    defaultLocationType={'P'}
                  />
                </Card>
              </Grid.Col>
            </Grid.Row>
          ) : (
            <Grid.Row>
              <Grid.Col>
                <LicenceLocationList
                  onFocus={() => submitRef.current.isDone || setSubmit(submitLocation)}
                  licenceId={props.licence.id ?? -1}
                  onRemove={(loc) => handleRemoveLocation(loc).then(() => continueEditingSpectrumDetails(formProps.values))}
                  isDone={isDone}
                  locationConfiguration={props.locationConfiguration}
                  onSave={() => {
                    setSubmit(submitLocation);
                    formProps.submitForm();
                  }}
                  renderRowAsError={(row) => {
                    return props.locationType === 'TRANSMIT'
                      ? !transmitLocationDoneSchema.isValidSync(row.item)
                      : !receiveLocationDoneSchema.isValidSync(row.item, { context: { classification: String(props.licence.classification) } });
                  }}
                  locationType={props.locationType}
                  isSatellite={true}
                  licenceClassification={props.licence.classification}
                  onViewLocation={setViewLocationId}
                  lastSaved={lastSaved}
                />
              </Grid.Col>
            </Grid.Row>
          )}
          <SpectrumDetailsForm
            onFocus={() => submitRef.current.isDone || setSubmit(submitSpectrum)}
            spectrum={spectrum as any}
            onSaveSpectrum={() => {
              setSubmit(submitSpectrum);
              formProps.submitForm();
            }}
            onRemoveSpectrum={handleRemoveSpectrum}
            isDone={isDone}
            lastSaved={lastSaved}
            title={spectrumSectionTitle}
          />
          {props.autoPopulateLocationId && (
            <Card>
              <SubSectionHeading title="Receive location details" />
              {isDone ? (
                <FormContext.Provider value={{ mode: 'VIEW' }}>
                  <Grid.Row>
                    <Grid.Col lg={3}>
                      <TextField name="locations[0].noiseTemperature" label="System receive noise temperature (K)" required />
                    </Grid.Col>
                  </Grid.Row>
                </FormContext.Provider>
              ) : (
                <Form
                  id="receive-location-noise"
                  onSubmit={() => {
                    setSubmit(submitLocation);
                    formProps.submitForm();
                  }}
                  showFormButtonsBottom
                  mode="EDIT"
                  hideBack
                  hideCancel
                  hideFormButtonsTop
                  disableErrorMessage
                >
                  <Grid.Row>
                    <Grid.Col lg={3}>
                      <TextField
                        name="locations[0].noiseTemperature"
                        label="System receive noise temperature (K)"
                        required
                        onFocus={() => submitRef.current.isDone || setSubmit(submitLocation)}
                      />
                    </Grid.Col>
                  </Grid.Row>
                </Form>
              )}
            </Card>
          )}
        </LocationSectionAccordion>
      )}
    </Formik>
  );
};

export const transmitLocationSectionTitle = 'Satellite Earth station details - Transmit location';
export const receiveLocationSectionTitle = 'Site details - Receive location';

const LocationSectionAccordion: React.FC<{
  locationType: 'TRANSMIT' | 'RECEIVE';
  expanded?: boolean;
  done?: boolean;
  onDone: () => void;
  onValidateDone: () => void;
  onExpandCollapsed?: () => void;
  licence?: RadioLicenceDto;
  children?: React.ReactNode;
  titleOverride?: string;
}> = (props) => {
  const { initialValues, errors } = useFormikContext<{ spectrum?: SpectrumDto; locations?: TransmitLocationDto[] | ReceiveLocationDto[] }>();
  const { validateSatelliteTransmitLocation, validateSatelliteReceiveLocation } = useDoneValidation();
  const [hasInitialError, setHasInitialError] = useState<boolean | undefined>();
  const hasError = !isEmpty(errors);
  const hasLocationDoneError = typeof errors?.locations === 'object';
  const hasSpectrumDetailsDoneError = typeof errors?.spectrum === 'object';
  const isInitialised = !!props.licence && !!initialValues.spectrum && !!initialValues.locations;
  const { craftingProgress } = props.licence ?? {};

  const isDone =
    props.locationType === 'TRANSMIT' ? !craftingProgress?.isTransmitLocationDetailsDone : !craftingProgress?.isReceiveLocationDetailsDone;
  const title = props.titleOverride ?? (props.locationType === 'TRANSMIT' ? transmitLocationSectionTitle : receiveLocationSectionTitle);
  const errorMessage = props.locationType === 'TRANSMIT' ? 'Invalid Transmit location' : 'Invalid Receive location';

  // initialErrors holds errors on load, whereas "errors" holds errors when Done is pressed
  useEffect(() => {
    if (isInitialised && !isDone && props.locationType === 'TRANSMIT') {
      validateSatelliteTransmitLocation(props.licence!).then((valid) => setHasInitialError(!valid));
    } else if (isInitialised && !isDone && props.locationType === 'RECEIVE') {
      validateSatelliteReceiveLocation(props.licence!).then((valid) => setHasInitialError(!valid));
    }
    // eslint-disable-next-line
  }, [isInitialised]);

  return (
    <Accordion
      {...props}
      id={props.locationType + '-location-section'}
      title={title}
      error={!props.done && isInitialised && (hasInitialError || hasError)}
    >
      {hasLocationDoneError && <Error errorMessage={errorMessage} showIcon />}
      {hasSpectrumDetailsDoneError && <Error errorMessage="Invalid Spectrum details" showIcon />}
      {props.children}
    </Accordion>
  );
};

export const stationTypeOptions: IDropdownOption[] = [
  { key: 'Gateway/Feeder link', text: 'Gateway/Feeder link' },
  { key: 'Terminal/User terminal', text: 'Terminal/User terminal' },
  { key: 'Other', text: 'Other' },
];

export const antennaPatternOptions: IDropdownOption[] = [
  { key: 'REC ITU-R S.465', text: 'REC ITU-R S.465' },
  { key: 'REC ITU-R S.580', text: 'REC ITU-R S.580' },
  { key: 'REC ITU-R S.672', text: 'REC ITU-R S.672' },
  { key: 'REC ITU-R S.1855', text: 'REC ITU-R S.1855' },
  { key: 'REC ITU-R S.1528', text: 'REC ITU-R S.1528' },
  { key: 'Other', text: 'Other (please state) [e.g. Yagi, or omni]' },
];

export const emptyFrequencyObj = {
  referenceFrequency: undefined,
  referenceFrequencyType: undefined,
  power: undefined,
  powerType: undefined,
  frequencyEmissions: [],
  isoStartTime: undefined,
  isoStopTime: undefined,
  hourCode: undefined,
};

export const transformSpectrumDto = (
  dto: MaintainFrequencySpectrumDto & CreateFrequencySpectrumDto,
): MaintainFrequencySpectrumDto & CreateFrequencySpectrumDto => {
  if (dto.frequencies && isUndefinedObject(dto.frequencies[0])) {
    dto = {
      ...dto,
      frequencies: undefined,
    };
  }
  return dto;
};

const isUndefinedObject = (object?: any): boolean => {
  if (!object) return true;
  for (const key in object) {
    // eslint-disable-next-line
    if (object[key] != undefined && (!Array.isArray(object[key]) || object[key].length > 0)) {
      return false;
    }
  }
  return true;
};

export default LocationSection;
